<script setup lang="ts">
import { computed, onMounted } from 'vue';
import AuthScreenLayout from '@/layout/AuthScreenLayout.vue';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import { authFlow } from '@/integrations/Immersve/auth/AuthFlow';
import { announceSessionChange } from '@/integrations/Immersve/auth/sessionChangeSignal';
import { transientState } from '@/integrations/Immersve/auth/TransientState';

const DEFAULT_RETURN_TO = '/aml-review';

type GoogleCallbackErrorCode =
  | 'consent-not-granted'
  | 'login-request-expired'
  | 'login-request-already-used';

const ERROR_MESSAGES: Record<GoogleCallbackErrorCode, string> = {
  'consent-not-granted':
    "Sign-in wasn't completed — the Google prompt needs to be approved to continue.",
  'login-request-expired': 'That sign-in link has expired.',
  'login-request-already-used': 'That sign-in link has already been used.',
};

const errorCode = new URLSearchParams(window.location.search).get('error');

const errorMessage = computed(() => {
  if (!errorCode) return null;
  return ERROR_MESSAGES[errorCode as GoogleCallbackErrorCode] ?? 'Sign-in failed.';
});

function retry() {
  authFlow.enter();
}

onMounted(() => {
  if (errorCode) return;
  announceSessionChange();
  transientState.clearEstablishAttempt();
  const returnTo = transientState.takeReturnTo() || DEFAULT_RETURN_TO;
  window.location.href = returnTo;
});
</script>

<template>
  <AuthScreenLayout>
    <div class="flex flex-col items-center" data-test="screen-auth-resume">
      <template v-if="errorMessage">
        <ExclamationTriangleIcon class="mb-3 size-10 text-gray-400" aria-hidden="true" />
        <h1 class="mb-4 text-3xl font-bold text-gray-900">Couldn't sign you in</h1>
        <p class="mb-6 text-gray-500" data-test="resume-error-message">{{ errorMessage }}</p>
        <button
          type="button"
          data-test="btn-retry-sign-in"
          class="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700"
          @click="retry"
        >
          Try again
        </button>
      </template>
      <template v-else>
        <ArrowPathIcon class="mb-3 size-10 animate-spin text-gray-400" aria-hidden="true" />
        <h1 class="mb-4 text-3xl font-bold text-gray-900">Signing you in</h1>
        <p class="text-gray-500">Just a moment...</p>
      </template>
    </div>
  </AuthScreenLayout>
</template>
